/**
 * The review step of a CSV import.
 *
 * The whole point of this screen is that nothing has happened yet. Column
 * guesses are wrong often enough that importing on the guess would write
 * hundreds of rows into the wrong month, so the mapping is shown, correctable,
 * and every row is visible with a tick next to it before anything is written.
 */

import { h } from '../vdom.js';
import { C, MONO, SANS, css, kicker, tap, button, expenseLabel } from './common.js';
import { CATEGORIES } from '../../../shared/model.js';

const FIELDS = ['date', 'label', 'amount', 'currency'];

export function renderImport(ctx) {
  const { state, t, actions } = ctx;
  const draft = state.importing;
  const s = t.shell;
  /*
   * On a phone the review is a full-screen sheet, not a floating card. The card
   * was 88vh tall, and on an iPhone vh is measured with Safari's bars hidden:
   * the footer sat under the toolbar, the page behind scrolled instead of the
   * rows, and the buttons wrapped into a ragged heap under a sentence.
   */
  const mobile = Boolean(ctx.mobile);

  const included = draft.entries.filter(entry => entry.include);
  const unusable = draft.entries.filter(entry => !entry.valid);
  /* Money moved between the person's own accounts, left out unless ticked back in. */
  const transfers = draft.entries.filter(entry => entry.valid && entry.transfer && !entry.include);
  /* What only a model could still answer: expenses nothing on this device recognised. */
  const unsorted = draft.entries.filter(
    entry => entry.valid && entry.kind === 'expense' && !entry.categorySource
  );

  return h('div', {
    key: 'import',
    onclick: event => {
      if (event.target === event.currentTarget) actions.closeImport();
    },
    style: css({
      position: 'fixed', inset: 0, zIndex: 70, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: mobile ? 0 : '18px',
      background: 'rgba(4,8,12,.78)',
      overflowY: mobile ? 'hidden' : 'auto', overscrollBehavior: 'contain'
    })
  },
    h('div', {
      role: 'dialog', 'aria-modal': 'true', 'aria-label': s.importTitle,
      style: css({
        width: '100%',
        maxWidth: mobile ? null : '620px',
        height: mobile ? '100%' : null,
        maxHeight: mobile ? null : '88vh',
        background: C.card,
        border: mobile ? 'none' : `1px solid ${C.stroke}`,
        borderRadius: mobile ? 0 : '16px',
        display: 'flex', flexDirection: 'column',
        fontFamily: SANS, color: C.text, overflow: 'hidden'
      })
    },
      // ---- header ----
      h('div', {
        style: css({
          padding: mobile ? 'calc(12px + env(safe-area-inset-top)) 16px 12px' : '18px 20px 14px',
          display: 'flex', alignItems: 'center', gap: '10px',
          borderBottom: `1px solid ${C.line}`, flex: '0 0 auto'
        })
      },
        h('div', { style: css({ flex: 1, minWidth: 0 }) },
          h('div', { style: css({ fontSize: '16px', fontWeight: 700 }) }, s.importTitle),
          h('div', {
            style: css({
              fontSize: '11px', color: C.faint, fontFamily: MONO, marginTop: '3px',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            })
          }, draft.name)
        ),
        h('div', {
          ...tap(actions.closeImport, s.close),
          style: css({
            width: '34px', height: '34px', flex: '0 0 34px', display: 'flex',
            alignItems: 'center', justifyContent: 'center', borderRadius: '11px',
            border: `1px solid ${C.stroke}`, color: C.muted, fontFamily: MONO, cursor: 'pointer'
          })
        }, '×')
      ),

      // ---- body: the only part that scrolls ----
      h('div', {
        class: 'pt-scroll',
        style: css({
          flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: mobile ? '14px 16px' : '16px 20px',
          display: 'flex', flexDirection: 'column', gap: '16px'
        })
      },
        monthSection(ctx, draft),
        h('div', { style: css({ height: '1px', background: C.line }) }),
        mappingSection(ctx, draft),
        h('div', { style: css({ height: '1px', background: C.line }) }),
        rowsSection(ctx, draft, unusable)
      ),

      footer(ctx, { mobile, included, unusable, transfers, unsorted })
    )
  );
}

/**
 * What will happen, and the buttons that make it happen.
 *
 * On a phone the sentence takes its own line and the buttons sit in a fixed
 * arrangement under it -- suggestions across the whole width, then Cancel and
 * Import side by side at thumb height -- instead of wrapping wherever their
 * label lengths happened to leave them. The bottom edge clears the iPhone's
 * home indicator.
 */
function footer({ state, t, actions }, { mobile, included, unusable, transfers, unsorted }) {
  const s = t.shell;
  const size = mobile
    ? { height: '44px', padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px' }
    : {};

  /*
   * Only where Liquidity+ offers it. It proposes categories on the rows
   * already on screen; nothing is written until IMPORT.
   */
  const sort = state.ai?.server?.available && button(
    unsorted.length ? s.aiSort + ' (' + unsorted.length + ')' : s.aiSort,
    actions.categoriseImport, {
      tone: 'muted',
      style: { ...size, flex: mobile ? '1 1 100%' : null, opacity: state.busy ? 0.5 : 1 }
    });
  const cancel = button(s.cancel, actions.closeImport, {
    tone: 'muted', style: { ...size, flex: mobile ? '1 1 0' : null }
  });
  const confirm = button(
    s.importConfirm(included.length),
    () => { if (included.length) actions.confirmImport(); },
    { style: { ...size, flex: mobile ? '2 1 0' : null, opacity: state.busy || !included.length ? 0.45 : 1 } }
  );
  const summary = h('div', {
    style: css({
      flex: mobile ? '1 1 100%' : 1, minWidth: 0, fontSize: '12px', color: C.muted, lineHeight: 1.45
    })
  }, s.importSummary(included.length, unusable.length, transfers.length));

  return h('div', {
    style: css({
      flex: '0 0 auto',
      padding: mobile ? '12px 16px calc(12px + env(safe-area-inset-bottom))' : '14px 20px',
      borderTop: `1px solid ${C.line}`,
      display: 'flex', alignItems: 'center', gap: mobile ? '8px' : '12px', flexWrap: 'wrap'
    })
  }, summary, sort, cancel, confirm);
}

/**
 * Which month the rows go into, said before anything is written.
 *
 * The month open in the app by default -- an expense added by hand lands there
 * too -- or each row's own date, for a statement covering several months. Rows
 * dated outside the chosen month are counted out loud, because filing August's
 * card payments under September is exactly the mistake nobody spots later.
 */
function monthSection({ t, actions }, draft) {
  const s = t.shell;
  const target = s.monthFull(draft.month);
  const elsewhere = draft.entries.filter(
    entry => entry.include && entry.month && entry.month !== draft.month
  );
  const months = [...new Set(elsewhere.map(entry => entry.month))].sort().map(s.monthFull);

  const choice = (mode, label) => {
    const on = draft.monthMode === mode;
    return h('div', {
      key: mode,
      ...tap(() => actions.setImportMonthMode(mode), label),
      'aria-pressed': on ? 'true' : 'false',
      class: 'pt-button',
      style: css({
        flex: '1 1 180px', padding: '9px 12px', borderRadius: '10px', cursor: 'pointer',
        border: `1px solid ${on ? C.accent : C.stroke}`,
        background: on ? C.accentDeep : 'transparent',
        color: on ? C.text : C.muted, fontSize: '12px', lineHeight: 1.35
      })
    }, label);
  };

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
    kicker(s.importMonthTitle, { fontSize: '9px', letterSpacing: '1.3px' }),
    h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
      choice('viewed', s.importMonthViewed(target)),
      choice('dated', s.importMonthDated)
    ),
    h('div', {
      style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
    }, draft.monthMode === 'dated' ? s.importMonthDatedNote : s.importMonthViewedNote(target)),
    draft.monthMode !== 'dated' && elsewhere.length > 0 && h('div', {
      key: 'elsewhere',
      style: css({ fontSize: '11px', color: C.accentHi, lineHeight: 1.45 })
    }, s.importMonthOutside(elsewhere.length, months.join(', ')))
  );
}

/**
 * Which column is which.
 *
 * Currency is allowed to be "none" - plenty of exports have one currency and no
 * column for it, and inventing one from a header that is not there is worse
 * than falling back to the account's own.
 */
function mappingSection({ t, state, actions }, draft) {
  const s = t.shell;

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
    kicker(s.importColumns, { fontSize: '9px', letterSpacing: '1.3px' }),
    h('div', {
      style: css({
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(132px,1fr))', gap: '9px'
      })
    },
      ...FIELDS.map(field => h('label', {
        key: field,
        style: css({ display: 'flex', flexDirection: 'column', gap: '5px' })
      },
        h('div', {
          style: css({ fontSize: '10px', color: C.faint, fontFamily: MONO, letterSpacing: '.6px' })
        }, s.importField[field]),
        h('select', {
          value: draft.mapping[field] == null ? '' : String(draft.mapping[field]),
          onchange: event => actions.setImportColumn(field, event.target.value),
          style: css({
            height: '36px', borderRadius: '9px', padding: '0 8px',
            background: C.bg, border: `1px solid ${C.stroke}`, color: C.text,
            fontFamily: SANS, fontSize: '12px', width: '100%'
          })
        },
          h('option', { value: '' }, s.importNoColumn),
          ...draft.header.map((name, index) => h('option', {
            key: index, value: String(index)
          }, name || `#${index + 1}`))
        )
      ))
    ),
    h('div', {
      style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
    }, s.importNote(state.settings.baseCurrency))
  );
}

/**
 * Where a row will land, where that answer came from, and a way to change it.
 *
 * The category used to be a line of text: right or wrong, it went in as it was,
 * and the only correction was editing each entry after the import. Now it is a
 * choice, and a choice made here is remembered for the payee.
 *
 * The select is keyed on its value because the renderer seeds a field once and
 * never writes it again -- an answer arriving from the model would otherwise
 * leave the old value on screen.
 */
function categoryLine({ t, actions }, entry) {
  const s = t.shell;
  const expense = entry.kind === 'expense';
  if (!expense && !entry.transfer) return null;

  return h('div', {
    key: 'category',
    style: css({
      flex: '1 1 100%', minWidth: 0, paddingLeft: '90px', marginTop: '-2px',
      display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap'
    })
  },
    expense && h('select', {
      key: 'pick-' + entry.line + '-' + (entry.category || '') + (entry.transfer ? '-t' : ''),
      value: entry.category || '',
      'aria-label': s.importCategory,
      onchange: event => actions.setImportCategory(entry.line, event.target.value),
      style: css({
        minHeight: '24px', maxWidth: '100%', minWidth: 0, borderRadius: '7px', padding: '2px 6px',
        background: C.bg, border: `1px solid ${entry.category ? C.accentDeep : C.stroke}`,
        color: entry.category ? C.accent : C.muted, fontFamily: MONO, fontSize: '10px'
      })
    },
      !entry.category && h('option', { key: 'none', value: '' }, s.importNoCategory),
      ...CATEGORIES.map(key => h('option', { key, value: key }, expenseLabel(t, key)))
    ),
    entry.transfer
      ? h('span', {
        key: 'transfer',
        style: css({ fontFamily: MONO, fontSize: '9.5px', color: C.muted })
      }, s.importTransfer)
      : entry.categorySource && h('span', {
        key: 'source',
        style: css({ fontFamily: MONO, fontSize: '9px', color: C.faint, letterSpacing: '.3px' })
      }, s.importSource[entry.categorySource] || '')
  );
}

function rowsSection({ t, fmt, actions }, draft, unusable) {
  const s = t.shell;
  const shown = draft.entries.slice(0, draft.preview);

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '7px' }) },
    kicker(s.importRows, { fontSize: '9px', letterSpacing: '1.3px' }),

    ...shown.map(entry => h('div', {
      key: entry.line,
      style: css({
        display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap',
        padding: '7px 0', borderBottom: `1px solid ${C.line}`,
        opacity: entry.valid ? 1 : 0.45
      })
    },
      /*
       * The tick doubles as the reason a row is out: a row the parser could not
       * read cannot be ticked, and says so when you reach for it.
       */
      h('div', {
        ...tap(
          () => actions.toggleImportRow(entry.line),
          entry.valid ? s.importInclude : s.importUnusable
        ),
        title: entry.valid ? '' : s.importUnusable,
        /* Says whether the row goes in, not only that it can be toggled. */
        'aria-pressed': String(Boolean(entry.include)),
        style: css({
          width: '18px', height: '18px', flex: '0 0 18px', borderRadius: '5px',
          border: `1px solid ${entry.include ? C.accent : C.stroke}`,
          background: entry.include ? C.accent : 'transparent',
          color: C.bg, fontFamily: MONO, fontSize: '11px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: entry.valid ? 'pointer' : 'default'
        })
      }, entry.include ? '✓' : ''),

      /*
       * The month the row will be filed under. Moved rows -- dated elsewhere,
       * going into the month open in the app -- are marked, with the date's
       * own month on hover.
       */
      h('div', {
        title: draft.monthMode !== 'dated' && entry.month && entry.month !== draft.month
          ? s.importFrom(entry.month)
          : null,
        style: css({
          fontFamily: MONO, fontSize: '10.5px', flex: '0 0 54px',
          color: draft.monthMode !== 'dated' && entry.month && entry.month !== draft.month
            ? C.accentHi
            : C.faint
        })
      }, !entry.month ? '—' : (draft.monthMode === 'dated' ? entry.month : draft.month)),

      h('div', {
        style: css({
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px'
        })
      },
        h('div', {
          style: css({
            fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          })
        }, entry.label),
        draft.duplicates?.has(entry.line) && h('span', {
          key: 'dup', style: css({ fontFamily: MONO, fontSize: '9.5px', color: C.muted })
        }, s.importDuplicate),
        entry.voided && !draft.duplicates?.has(entry.line) && h('span', {
          key: 'void', style: css({ fontFamily: MONO, fontSize: '9.5px', color: C.muted })
        }, s.importVoided)
      ),

      /*
       * Kind comes from the sign, which is right almost always and wrong for
       * refunds - so it stays a button rather than a verdict.
       */
      entry.valid && h('div', {
        ...tap(
          () => actions.setImportKind(entry.line, entry.kind === 'income' ? 'expense' : 'income'),
          s.importFlip
        ),
        style: css({
          fontFamily: MONO, fontSize: '8.5px', fontWeight: 700, letterSpacing: '.6px',
          padding: '4px 7px', borderRadius: '7px', cursor: 'pointer',
          border: `1px solid ${entry.kind === 'income' ? C.accent : C.danger}`,
          color: entry.kind === 'income' ? C.accent : C.danger
        })
      }, entry.kind === 'income' ? s.importIn : s.importOut),

      h('div', {
        style: css({
          fontFamily: MONO, fontSize: '12px', color: C.muted,
          flex: '0 0 auto', textAlign: 'right'
        })
      }, entry.amount === null ? '—' : fmt.money(entry.amount, 2, entry.currency)),

      /*
       * On a line of its own, lined up under the description: squeezed between
       * the description and the amount, a phone showed "Zaku…" for a category.
       */
      entry.valid && categoryLine({ t, actions }, entry)
    )),

    /*
     * Rows past the preview are imported like the rest -- and now that their
     * categories can be corrected, they can be shown too.
     */
    draft.entries.length > shown.length && h('div', {
      key: 'more',
      style: css({
        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', paddingTop: '6px'
      })
    },
      h('div', {
        style: css({ flex: '1 1 220px', fontSize: '11px', color: C.faint })
      }, s.importMore(draft.entries.length - shown.length)),
      button(s.importShowAll(draft.entries.length), actions.showAllImportRows, {
        tone: 'muted', style: { fontSize: '10px', padding: '6px 10px' }
      })
    ),

    draft.entries.some(entry => entry.valid && entry.kind === 'expense') && h('div', {
      key: 'learn',
      style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45, paddingTop: '6px' })
    }, s.importLearnNote),

    unusable.length > 0 && h('div', {
      style: css({ fontSize: '11px', color: C.muted, lineHeight: 1.45, paddingTop: '6px' })
    }, s.importUnusableNote(unusable.length))
  );
}
